import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useScrollReveal } from '../hooks/useScrollReveal'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/useAuth'
import { Alert } from '../components/Alert'
import { BbIcon } from '../components/BbIcon'

/* Markup follows prototypes/batchbooks_get_started/code.html. The register call
   and its error handling are unchanged.

   Deviations from the prototype, all of them forced by the API contract:
   - "Sign up with Intuit" and its divider are omitted; there is no Intuit
     OAuth sign-up endpoint in docs/api-contract.md.
   - The prototype collects "Full Name"; register() takes companyName, which the
     workspace is named after, so that is the field asked for instead. Adding a
     personal name means adding it to the contract first.
   - "Terms & Conditions" / "Privacy Policy" are not links — those pages do not
     exist yet. The consent checkbox is real and gates submission. */
export function RegisterPage() {
  const signupRef = useRef<HTMLDivElement>(null)
  useScrollReveal(signupRef)
  const { register } = useAuth()
  const navigate = useNavigate()
  const [companyName, setCompanyName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await register(email, password, companyName)
      navigate('/app', { replace: true })
    } catch (cause: unknown) {
      setError(cause instanceof ApiError ? cause.message : 'Could not reach the server.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="bb">
      <div className="bb-signup" ref={signupRef}>
        <div className="bb-signup-card">
          <aside className="bb-signup-aside bb-scroll-reveal">
            <div className="bb-signup-aside-in">
              <h1>BatchBooks</h1>
              <p className="bb-signup-aside-tag">Bulk Data Management</p>
              <h2>Streamline your accounting workflows.</h2>

              <ul className="bb-benefits">
                <li className="bb-benefit">
                  <BbIcon name="bolt" />
                  <div>
                    <h3>Automate in 5 minutes</h3>
                    <p>Set up complex data imports without writing a single line of code.</p>
                  </div>
                </li>
                <li className="bb-benefit">
                  <BbIcon name="card-off" />
                  <div>
                    <h3>No credit card required</h3>
                    <p>Start managing your bulk data immediately with our free tier.</p>
                  </div>
                </li>
                <li className="bb-benefit">
                  <BbIcon name="lock" />
                  <div>
                    <h3>Secure Intuit OAuth</h3>
                    <p>Bank-level security ensuring your financial data remains protected.</p>
                  </div>
                </li>
              </ul>
            </div>

            <figure className="bb-quote bb-scroll-reveal bb-scroll-reveal-d2">
              <span className="bb-quote-mark" aria-hidden="true">
                SJ
              </span>
              <div>
                <p>&ldquo;BatchBooks saved our firm hours of manual data entry every week.&rdquo;</p>
                <figcaption className="bb-quote-who">Sarah J., Lead Accountant</figcaption>
              </div>
            </figure>
          </aside>

          <main className="bb-signup-main bb-scroll-reveal bb-scroll-reveal-d1">
            <div className="bb-signup-head">
              <h2>Get Started</h2>
              <p>Create your account to start managing bulk data efficiently.</p>
            </div>

            {error ? (
              <Alert tone="err" title="Could not create the workspace">
                {error}
              </Alert>
            ) : null}

            <form onSubmit={submit}>
              <div className="bb-field">
                <label className="bb-flabel" htmlFor="companyName">
                  Firm or company name <span className="bb-req">*</span>
                </label>
                <input
                  className="bb-input"
                  id="companyName"
                  required
                  placeholder="Apex Accounting"
                  autoComplete="organization"
                  value={companyName}
                  onChange={(event) => setCompanyName(event.target.value)}
                />
              </div>

              <div className="bb-field">
                <label className="bb-flabel" htmlFor="email">
                  Work Email <span className="bb-req">*</span>
                </label>
                <input
                  className="bb-input"
                  id="email"
                  type="email"
                  required
                  placeholder="jane@company.com"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>

              <div className="bb-field">
                <label className="bb-flabel" htmlFor="password">
                  Password <span className="bb-req">*</span>
                </label>
                <input
                  className="bb-input"
                  id="password"
                  type="password"
                  required
                  minLength={8}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>

              <div className="bb-check-row" style={{ marginTop: 20 }}>
                <input
                  className="bb-check"
                  id="terms"
                  type="checkbox"
                  required
                  checked={agreed}
                  onChange={(event) => setAgreed(event.target.checked)}
                />
                <label className="bb-check-label" htmlFor="terms">
                  I agree to the Terms &amp; Conditions and Privacy Policy.
                </label>
              </div>

              <button
                className="bb-btn bb-btn-green bb-btn-block"
                type="submit"
                disabled={busy}
                style={{ marginTop: 24 }}
              >
                {busy ? 'Creating…' : 'Create Account'}
              </button>
            </form>

            <p className="bb-signup-alt">
              Already have an account? <Link to="/login">Log in</Link>
            </p>
          </main>
        </div>
      </div>
    </div>
  )
}
